import styled from 'styled-components'
import DeckGL from '@deck.gl/react/typed'

import {
  ArcLayer,
  GeoJsonLayer,
  TextLayer,
  SolidPolygonLayer,
} from '@deck.gl/layers/typed'
import { TripsLayer } from '@deck.gl/geo-layers/typed'

import {
  _GlobeView as GlobeView,
  MapView,
  AmbientLight,
  PointLight,
  LightingEffect,
} from '@deck.gl/core/typed'
import { FlyToInterpolator, COORDINATE_SYSTEM } from 'deck.gl/typed'
import { BASEMAP } from '@deck.gl/carto/typed'
import { Map } from 'react-map-gl'
import data from './grouped_connections.json'
import { useState, useEffect, useCallback, useContext } from 'react'
import { useEffectOnce } from 'usehooks-ts'
import 'mapbox-gl/dist/mapbox-gl.css'
import worldmap from './countries-min9.json'
import InfoBoard from '../InfoBoard'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import GlobalContext from 'Contexts/GlobalContext'
// trips
import trip_gigi_oeri from './detailed_trip_gigi_oeri_formatted.json'
import trip_hayek from './detailed_trip_hayek_formatted.json'
import trip_rolex1 from './trip_rolex1_formatted.json'
import trip_rolex2 from './trip_rolex2_formatted.json'
import trip_rolex_combined from './trip_rolex_timestamps_combined_formatted.json'
import trip_frey from './detailed_trip_frey_formatted.json'

const FLIGHTS = [
  trip_gigi_oeri,
  trip_hayek,
  trip_frey,
  trip_rolex1,
  trip_rolex2,
]
const VIEWSTATES_MOBILE = [
  {
    longitude: 6.3093,
    latitude: 43.0502,
    zoom: 3.5,
    pitch: 0,
    bearing: 0,
    transitionDuration: 8000,
    transitionInterpolator: new FlyToInterpolator(),
  },
  {
    longitude: 6.3093,
    latitude: 43.0502,
    zoom: 3.3,
    pitch: 0,
    bearing: 0,
    transitionDuration: 8000,
    transitionInterpolator: new FlyToInterpolator(),
  },
  {
    longitude: 15,
    latitude: 50,
    zoom: 3.3,
    pitch: 0,
    bearing: 0,
    transitionDuration: 8000,
    transitionInterpolator: new FlyToInterpolator(),
  },
  {
    longitude: 6.3093,
    latitude: 43.0502,
    zoom: 2.8,
    pitch: 0,
    bearing: 0,
    transitionDuration: 8000,
    transitionInterpolator: new FlyToInterpolator(),
  },
]
const VIEWSTATES_DESKTOP = [
  {
    longitude: 6.3093,
    latitude: 43.0502,
    zoom: 3.5,
    pitch: 0,
    bearing: 0,
    transitionDuration: 8000,
    transitionInterpolator: new FlyToInterpolator(),
  },
  {
    longitude: 6.3093,
    latitude: 43.0502,
    zoom: 3.5,
    pitch: 0,
    bearing: 0,
    transitionDuration: 8000,
    transitionInterpolator: new FlyToInterpolator(),
  },
  {
    longitude: 6.3093,
    latitude: 43.0502,
    zoom: 3.5,
    pitch: 0,
    bearing: 0,
    transitionDuration: 8000,
    transitionInterpolator: new FlyToInterpolator(),
  },
  {
    longitude: 6.3093,
    latitude: 43.0502,
    zoom: 3.5,
    pitch: 0,
    bearing: 0,
    transitionDuration: 8000,
    transitionInterpolator: new FlyToInterpolator(),
  },
]

// // Viewport settings
// const INITIAL_VIEW_STATE: {
//   longitude: number
//   latitude: number
//   zoom: number
//   pitch: number
//   bearing: number
// } = {
//   longitude: -8.3093,
//   latitude: 30.0502,
//   zoom: 1,
//   pitch: 0,
//   bearing: 0,
// }

const ambientLight = new AmbientLight({
  color: [255, 255, 255],
  intensity: 1.0,
})

const pointLight = new PointLight({
  color: [255, 255, 255],
  intensity: 2.0,
  position: [-74.05, 40.7, 8000],
})

const lightingEffect = new LightingEffect({ ambientLight, pointLight })

const material = {
  ambient: 0.1,
  diffuse: 0.6,
  shininess: 32,
  specularColor: [60, 64, 70],
}

const DEFAULT_THEME = {
  buildingColor: [74, 80, 87],
  trailColor0: [253, 128, 93],
  trailColor1: [23, 184, 190],
  material,
  effects: [lightingEffect],
}

const FlightPaths = ({
  colorMode,
  scrollIndex,
}: {
  colorMode: 'light' | 'dark'
  scrollIndex: number
}) => {
  const { currentTime, setCurrentTime, setCurrentDistance, setScrollIndex } =
    useContext(GlobalContext)
  useEffect(() => {
    console.log('useEffect inside FlightPaths', '[scrollIndex]', scrollIndex)
    setScrollIndex(scrollIndex)
    setCount(0)
  }, [scrollIndex])
  const [geoJsonWorldLandData, setGeoJsonWorldLandData] = useState<any>(null)
  const [initialViewState, setInitialViewState] = useState<any>({
    longitude: 6.3093,
    latitude: 43.0502,
    zoom: 3.5,
    pitch: 0,
    bearing: 0,
  })
  const [count, setCount] = useState(0)
  const [animation] = useState<any>({})
  const [tripLayers, setTripLayers] = useState<TripsLayer[]>([])
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [viewState, setViewState] = useState<any>(initialViewState)
  const trailLength = 2000

  const animationSpeed = 1
  const theme = DEFAULT_THEME
  const textLayerContent = [
    {
      name: 'Colma (COLM)',
      coordinates: [-122.466233, 37.684638],
    },
  ]

  useEffect(() => {
    // console.log('useEffect', '[count]')

    let currentFlight
    if (scrollIndex === 3) {
      currentFlight = trip_rolex_combined?.[0]
    } else {
      currentFlight = FLIGHTS[scrollIndex]?.[0]
    }
    if (!currentFlight) return
    const newFlightPosition =
      currentFlight.timeStamps[count % currentFlight.timeStamps.length]
    const newTime = newFlightPosition.t
    const newDistance = newFlightPosition.d
    setCurrentTime(newTime)
    setCurrentDistance('' + newDistance)
  }, [count])

  useEffect(() => {
    // console.log('useEffect', '[animation]')
    animation.id = window.requestAnimationFrame(animate)
    // return () => window.cancelAnimationFrame(animation.id)
  }, [animation])
  const animate = () => {
    // console.log('animate')
    // setTimeout(() => {
    // }, 1000 / 60)
    setCount((count) => {
      const countIncremented = count + animationSpeed
      // console.log('setCount', { count, countIncremented, animationSpeed })
      return countIncremented
    })

    animation.id = window.requestAnimationFrame(animate)
  }

  async function initialize() {
    console.log('initialize')
    setGeoJsonWorldLandData(worldmap)
  }
  useEffect(() => {
    // console.log('useEffect', '[scrollIndex]')
    const newViewState = isMobile
      ? VIEWSTATES_MOBILE[scrollIndex]
      : VIEWSTATES_DESKTOP[scrollIndex]
    setInitialViewState(newViewState)
  }, [scrollIndex])

  useEffect(() => {
    // console.log('useEffect', '[]')
    initialize()
    setIsMobile(window.innerWidth < 600)
  }, [])
  useEffect(() => {
    // console.log('useEffect', '[scrollIndex, currentTime]')
    const flight = FLIGHTS[scrollIndex]
    const secondFlight =
      scrollIndex === 3 ? FLIGHTS[scrollIndex + 1] : undefined
    const newTripLayers = []
    flight &&
      newTripLayers.push(
        new TripsLayer({
          id: 'trips' + scrollIndex,
          data: flight,
          getPath: (d) => {
            return d?.path
          },
          getTimestamps: (d) =>
            d.timeStamps.map((ts: { t: number; d: number }) => ts.t),
          opacity: 1,
          getColor:
            scrollIndex !== 3
              ? scrollIndex !== 2
                ? scrollIndex === 0
                  ? [55, 142, 189, 255]
                  : [225, 109, 0, 255]
                : [167, 87, 163, 255]
              : [205, 0, 0, 255],
          widthMinPixels: 8,
          capRounded: true,
          jointRounded: true,
          trailLength,
          currentTime: currentTime,
          shadowEnabled: false,
          fadeTrail: true,
        })
      )

    secondFlight &&
      newTripLayers.push(
        new TripsLayer({
          id: 'trips' + scrollIndex + 1,
          data: secondFlight,
          getPath: (d) => {
            return d.path
          },
          getTimestamps: (d) =>
            d.timeStamps.map((ts: { t: number; d: number }) => ts.t),
          opacity: 1,
          getColor: [205, 0, 0, 255],
          widthMinPixels: 8,
          capRounded: true,
          jointRounded: true,
          trailLength,
          currentTime: currentTime,
          shadowEnabled: false,
          fadeTrail: true,
        })
      )
    setTripLayers(newTripLayers)
  }, [scrollIndex, currentTime])

  const isDarkMode = colorMode === 'dark'
  // show loading state while waiting for all the data
  if (!geoJsonWorldLandData) return <p>'loading'</p>
  return (
    <>
      <DeckGLWrapper>
        <DeckGL
          style={{
            // position: 'absolute',
            width: '100vw',
            height: '100vh',
            zIndex: '-99',
          }}
          effects={theme.effects}
          initialViewState={initialViewState}
          controller={false}
          layers={[
            // Background
            new SolidPolygonLayer({
              id: 'background',
              data: [
                [
                  [-180, 90],
                  [0, 90],
                  [180, 90],
                  [180, -90],
                  [0, -90],
                  [-180, -90],
                ],
              ],
              getPolygon: (d) => d,
              stroked: false,
              filled: true,
              getFillColor: isDarkMode ? [18, 18, 18] : [245, 246, 247],
            }),
            // new ArcLayer({
            //   id: 'line-layer',
            //   greatCircle: true,
            //   data,
            //   getWidth: (d) => d.width,
            //   pickable: false,
            //   getHeight: 0.5,
            //   getSourceColor: [41, 106, 141, 150],
            //   getTargetColor: [41, 106, 141, 150],
            // }),
            new GeoJsonLayer({
              id: 'earth-land-layer',
              data: worldmap,
              stroked: true,
              filled: true,
              getFillColor: isDarkMode ? [100, 100, 100] : [229, 229, 229],
              getLineColor: isDarkMode ? [18, 18, 18] : [150, 150, 150],
              extruded: false,
              lineWidthMinPixels: 0.5,
              getLineWidth: 0.5,
            }),
            ...tripLayers,
          ]}
        >
          {/* <Map
          mapStyle={BASEMAP.POSITRON}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        /> */}
        </DeckGL>
      </DeckGLWrapper>
    </>
    // <DeckGL
    //   style={{
    //     position: 'absolute',
    //     width: '100vw',
    //     height: '100vh',
    //     zIndex: '-99',
    //   }}
    //   views={views}
    //   initialViewState={initialViewState}
    //   controller={true}
    //   layers={[
    //     new SolidPolygonLayer({
    //       id: 'background',
    //       data: [
    //         [
    //           [-180, 90],
    //           [0, 90],
    //           [180, 90],
    //           [180, -90],
    //           [0, -90],
    //           [-180, -90],
    //         ],
    //       ],
    //       getPolygon: (d) => d,
    //       stroked: false,
    //       filled: true,
    //       getFillColor: [205, 226, 238],
    //     }),
    //     new ArcLayer({
    //       id: 'line-layer',
    //       greatCircle: true,
    //       data,
    //       getWidth: (d) => d.width,
    //       pickable: false,
    //       getHeight: 0.5,
    //       getSourceColor: [41, 106, 141, 150],
    //       getTargetColor: [41, 106, 141, 150],
    //     }),
    //     new GeoJsonLayer({
    //       id: 'earth-land-layer',
    //       data: geoJsonWorldLandData,
    //       stroked: true,
    //       filled: true,
    //       getFillColor: [160, 156, 138],
    //       getLineColor: [0, 0, 0],
    //     }),
    //   ]}
    //   onLoad={rotateCamera}
    // />
  )
}
const DeckGLWrapper = styled.div`
  pointer-events: none;
  #deckgl-wrapper {
    position: sticky !important;
  }
`
const FrontElement = styled.div<{ [k: string]: any }>`
  font-size: 18px;
  line-height: 23px;
  font-family: var(--font-sans-serif);
  /* min-height: 100vh; */
  position: relative;
  z-index: 1001;
  background-color: #fff;
  p,
  h4,
  h3 {
    font-family: var(--font-sans-serif);
  }
  img {
    /* z-index: 0; */
    max-width: 100%;
  }
  p {
    background-color: var(--site-background);
    padding: 1.5em;
    border: 1px;
    border-color: #000000e8;
    /* text-align:  center; */
    line-height: 1.5em;
    box-shadow: rgba(0, 0, 0, 0.2) 0px 0px 10px 0px,
      rgba(255, 255, 255, 0.2) 0px 0px 10px 0px;
    -webkit-box-shadow: rgba(0, 0, 0, 0.2) 0px 0px 10px 0px,
      rgba(255, 255, 255, 0.2) 0px 0px 10px 0px;
    border-radius: 5px;
  }
`

const Label = styled.div`
  position: relative;
  top: 0;
  left: 0;
  padding: 1em;
  width: 100%;
`

const BackElement = styled.div`
  /* padding-top: 4vh; */
  @media only screen and (max-width: 542px) {
    padding-top: 0;
  }
  img {
    @media only screen and (max-width: 542px) {
    }
  }
  div {
    position: absolute;
    width: 100vw;
    background-size: contain;
    background-position: center, center;
    background-repeat: no-repeat;
    transition: opacity 0.6s linear;
    -webkit-transition: opacity 0.6s linear;
  }
  /* transition: opacity 1s ease-out; */
`

export default FlightPaths
